import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, CheckCircle2, Copy, type LucideIcon } from 'lucide-react';
import { Spinner } from '../Spinner';
import './setup.css';

/**
 * The settings kit: what a settings screen in the console is built from.
 *
 * Written for the analytics install screen and lifted out, unchanged, when the
 * Chatbot screen became the second screen of the same shape — a rail of named
 * sections, one section at a time, the why beside the control, code to copy,
 * one save bar per section, and a setup laid out as the steps it is done in.
 * Its styles are `setup.css`; a screen that is not inside `.comms` or
 * `.analytics` puts `setup-tokens` on its root to supply their vocabulary.
 */

/** One entry in a settings rail. */
export interface RailEntry<K extends string> {
  key: K;
  label: string;
  hint: string;
  icon: LucideIcon;
}

/**
 * A section's place in the rail: its name, what it is for, and one dot — unsaved
 * edits, or failing that the section's own state. The edit dot wins: it is the
 * one that costs something to ignore.
 */
export function RailItem<K extends string>({
  entry,
  on,
  dirty = false,
  status = null,
  onGo,
}: {
  entry: RailEntry<K>;
  on: boolean;
  dirty?: boolean;
  status?: { tone: 'ok' | 'warn'; label: string } | null;
  onGo: (key: K) => void;
}) {
  const Icon = entry.icon;
  return (
    <button
      type="button"
      className={on ? 'an-manage-navitem is-on' : 'an-manage-navitem'}
      // The capture script aims at a section by name, which is far steadier
      // than nth-child — the same hook the Settings rail carries.
      data-section={entry.key}
      aria-current={on ? 'page' : undefined}
      onClick={() => onGo(entry.key)}
    >
      <Icon size={15} />
      <span className="an-manage-navitem-main">
        <span className="an-manage-navitem-label">{entry.label}</span>
        <span className="an-manage-navitem-hint">{entry.hint}</span>
      </span>
      {dirty ? (
        <span className="an-manage-navdot is-dirty" role="img" aria-label="Unsaved changes" title="Unsaved changes" />
      ) : status ? (
        <span className={`an-manage-navdot ${status.tone}`} role="img" aria-label={status.label} title={status.label} />
      ) : null}
    </button>
  );
}


/** A section's heading: what it is for, in a line, above its sheets. */
export function SectionHead({ title, children }: { title: string; children: ReactNode }) {
  return (
    <header className="an-set-head">
      <h2 className="an-set-title">{title}</h2>
      <p className="an-set-sub">{children}</p>
    </header>
  );
}

/** Related settings on one sheet. */
export function Group({
  title,
  sub,
  danger = false,
  children,
}: {
  title?: string;
  sub?: ReactNode;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={danger ? 'an-set-group is-danger' : 'an-set-group'}>
      {title ? (
        <header className="an-set-group-head">
          <h3>{title}</h3>
          {sub ? <p>{sub}</p> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/**
 * One setting: what it is and why on the left, the control on the right.
 *
 * The why used to be small grey type under the control, where nobody reads —
 * and most of these settings are only safe to change once it has been read. At
 * reading width it gets a column of its own; in a narrow frame the two stack.
 * That turn is a container query on the pane rather than a media query,
 * because inside the dashboard this screen is a frame of whatever width the
 * host gives it.
 */
export function Row({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="an-set-row">
      <div className="an-set-what">
        {htmlFor ? (
          <label className="an-set-label" htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <span className="an-set-label">{label}</span>
        )}
        {hint ? <div className="an-set-hint">{hint}</div> : null}
      </div>
      <div className="an-set-control">{children}</div>
    </div>
  );
}

/** A bare switch for a Row, which already says what it switches. */
export function Switch({
  id,
  checked,
  disabled = false,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="toggle an-switch">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true" />
      <span className="an-switch-state" aria-hidden="true">
        {checked ? 'On' : 'Off'}
      </span>
    </label>
  );
}

/**
 * One of a few answers, each with what choosing it costs.
 *
 * A trade-off reads as one when both sides are on screen at once — as a
 * toggle, "Cookieless" hid what the OTHER mode is and what it is for.
 */
export function Choice<T extends string>({
  name,
  value,
  current,
  onChange,
  title,
  children,
}: {
  name: string;
  value: T;
  current: T;
  onChange: (next: T) => void;
  title: string;
  children: ReactNode;
}) {
  const on = value === current;
  return (
    <label className={on ? 'an-choice is-on' : 'an-choice'}>
      <input type="radio" name={name} value={value} checked={on} onChange={() => onChange(value)} />
      <span className="an-choice-mark" aria-hidden="true" />
      <span className="an-choice-text">
        <span className="an-choice-title">{title}</span>
        <span className="an-choice-sub">{children}</span>
      </span>
    </label>
  );
}

const COPY_KEYS = /Mac|iPhone|iPad/.test(typeof navigator === 'undefined' ? '' : navigator.userAgent)
  ? '⌘C'
  : 'Ctrl+C';

/** Code to paste, under a bar that names the language and holds Copy. */
export function CodeBlock({
  code,
  lang,
  label,
  primary = false,
}: {
  code: string;
  lang: string;
  label: string;
  primary?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'selected'>('idle');
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (state !== 'copied') return;
    const timer = setTimeout(() => setState('idle'), 2200);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setState('copied');
    } catch {
      // The clipboard needs a secure context and, inside the dashboard's frame,
      // the host's permission. Selecting the code leaves it one keystroke away
      // instead of a button that silently did nothing.
      const node = codeRef.current;
      const selection = window.getSelection();
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setState('selected');
    }
  };

  return (
    <div className="an-code">
      <div className="an-code-bar">
        <span className="an-code-lang">{lang}</span>
        <button
          type="button"
          className={primary ? 'btn btn-primary an-code-copy' : 'btn btn-secondary an-code-copy'}
          aria-label={`Copy ${label}`}
          onClick={() => void copy()}
        >
          {state === 'copied' ? <Check size={13} /> : <Copy size={13} />}
          {state === 'copied' ? 'Copied' : state === 'selected' ? `Selected — press ${COPY_KEYS}` : 'Copy'}
        </button>
      </div>
      <pre className="an-code-body">
        <code ref={codeRef}>{code}</code>
      </pre>
    </div>
  );
}

/**
 * The one way out of a settings section, pinned to the foot of the window.
 *
 * Three sheets per section each used to carry its own Save, and a change made
 * on one and not saved was lost the moment another section opened. One bar
 * per section, always in reach, saying whether anything is waiting.
 */
export function SaveBar({
  dirty,
  busy,
  saved,
  blocked,
  savedNote,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  busy: boolean;
  saved: boolean;
  blocked: boolean;
  savedNote: string;
  onSave: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className={dirty ? 'an-savebar is-dirty' : 'an-savebar'}>
      <span className="an-savebar-state" aria-live="polite">
        {dirty ? (
          blocked ? (
            'Some entries above need fixing before this can be saved.'
          ) : (
            'You have unsaved changes.'
          )
        ) : saved ? (
          <>
            <CheckCircle2 size={14} /> {savedNote}
          </>
        ) : (
          'No unsaved changes.'
        )}
      </span>
      <div className="an-savebar-actions">
        {dirty ? (
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={onDiscard}>
            Discard
          </button>
        ) : null}
        <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={onSave}>
          {busy ? <Spinner /> : null}
          Save changes
        </button>
      </div>
    </div>
  );
}

export function FlowStep({
  n,
  done = false,
  title,
  tag,
  children,
}: {
  n: number;
  done?: boolean;
  title: string;
  tag: { label: string; tone?: 'ok' | 'accent' | 'plain' };
  children: ReactNode;
}) {
  return (
    <li className={done ? 'an-flow-step is-done' : 'an-flow-step'}>
      <span className="an-flow-num" aria-hidden="true">
        {done ? <Check size={14} /> : n}
      </span>
      <div className="an-flow-body">
        <div className="an-flow-head">
          <h3>{title}</h3>
          <span className={`an-flow-tag ${tag.tone ?? 'plain'}`}>{tag.label}</span>
        </div>
        {children}
      </div>
    </li>
  );
}
